import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Box, Toolbar, Grid, Typography } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { VehicleData } from "../../types/vehicleData";
import VehicleCard from "./detalhesVeiculo";
import HeaderVehicle from "../../components/veiculos/headerVehicle";
import FilterVehicle from "../../components/veiculos/FilterVehicle";
import ModalAddVehicle from "../../components/veiculos/AddVehicleModal";
import {
  getVehiclesEntregues,
  getVehiclesNaoEntregue,
  getVehiclesSemChecklist,
  getVehiclesWithChecklistAtrasado,
  searchVehicles,
} from "../../api/vehicleApi";
import { getEmpresasData } from "../../api/dashboardApi";

const defaultTheme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
});

export type CheckboxState = {
  veiculosEntregues: boolean;
  dataArmazenagem: boolean;
  dataChecklist: boolean;
  dataSemChecklist: boolean;
  dataChecklistAtrasado: boolean;
};

const Veiculos: React.FC = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [loadingVehicles, setLoadingVehicles] = useState<boolean>(false);
  const [vehicles, setVehicles] = useState<VehicleData[]>([]);
  const [expandedPaperId, setExpandedPaperId] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [startDateChecklist, setStartDateChecklist] = useState<any>(null);
  const [endDateChecklist, setEndDateChecklist] = useState<any>(null);
  const [startDateArmazenagem, setStartDateArmazenagem] = useState<any>(null);
  const [endDateArmazenagem, setEndDateArmazenagem] = useState<any>(null);
  const [comCliente, setComCliente] = useState<boolean | null>(null);
  const [openAddVehicleModal, setOpenAddVehicleModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [selectedCompanyIdModal, setSelectedCompanyIdModal] = useState<
    number | null
  >(null);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkboxState, setCheckboxState] = useState<CheckboxState>({
    veiculosEntregues: false,
    dataArmazenagem: false,
    dataChecklist: false,
    dataSemChecklist: false,
    dataChecklistAtrasado: false,
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalVehicles, setTotalVehicles] = useState<number | null>(0);
  const token = localStorage.getItem("token");
  const queryParams = new URLSearchParams(search);
  const idEmpresa = queryParams.get("id_empresa");
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [shouldUpdateUrl, setShouldUpdateUrl] = useState(true);
  const [lastFetchedPage, setLastFetchedPage] = useState<number | null>(null);
  const requestSequenceRef = useRef(0);

  const handlePaperClick = (id: number) => {
    setExpandedPaperId((prevId) => (prevId === id ? null : id));
  };

  const handleEditButtonClick = (vehicleId: number) => {
    navigate(`/editarVeiculo/${vehicleId}`);
  };

  const handleAddClientButtonClick = (vehicleId: number) => {
    navigate(`/cliente/${vehicleId}`);
  };

  const handleViewClientButtonClick = (vehicleId: number, clientId: number) => {
    navigate(`/visualizarClienteVinculado/${vehicleId}/${clientId}`);
  };

  const handleChecklistButtonClick = (
    checklistId: string,
    vehicleId: string,
    idChecklistRespondido?: string
  ) => {
    navigate(
      `/checklist/${checklistId}/${vehicleId}${
        idChecklistRespondido ? `?id_respondido=${idChecklistRespondido}` : ""
      }`
    );
  };

  const handleCloseAddVehicleModal = () => {
    setOpenAddVehicleModal(false);
  };

  useEffect(() => {
    const params = new URLSearchParams(search);
    const pageParam = params.get("page");
    const empresaParam = params.get("id_empresa");
    const filterParam = params.get("filter");

    if (pageParam) setCurrentPage(Number(pageParam));
    if (empresaParam) setSelectedCompanyId(Number(empresaParam));

    if (filterParam === "atrasados") {
      setCheckboxState((prev) => ({
        ...prev,
        dataChecklistAtrasado: true,
        veiculosEntregues: false,
        dataSemChecklist: false,
      }));
    } else if (filterParam === "sem_checklist") {
      setCheckboxState((prev) => ({
        ...prev,
        dataSemChecklist: true,
        veiculosEntregues: false,
        dataChecklistAtrasado: false,
      }));
    }
  }, [search]);

  const fetchVehicles = useCallback(
    async (
      page: number = 1,
      search: string = "",
      startDateChecklist?: string,
      endDateChecklist?: string,
      startDateArmazenagem?: string,
      endDateArmazenagem?: string
    ) => {
      if (isFetching) return;
      const requestId = ++requestSequenceRef.current;
      setIsFetching(true);
      setLoadingVehicles(true);
      try {
        if (!token) {
          throw new Error("Token de autenticação não encontrado");
        }

        let data;

        if (search.trim().length > 0) {
          data = await searchVehicles(
            token,
            search,
            page,
            comCliente,
            selectedCompanyId
          );
        } else if (checkboxState.veiculosEntregues) {
          data = await getVehiclesEntregues(
            token,
            selectedCompanyId,
            page,
            comCliente === true
              ? true
              : comCliente === false
              ? false
              : undefined,
            startDateChecklist,
            endDateChecklist,
            startDateArmazenagem,
            endDateArmazenagem
          );
        } else if (checkboxState.dataChecklistAtrasado) {
          data = await getVehiclesWithChecklistAtrasado(
            token,
            selectedCompanyId,
            page,
            comCliente === true
              ? true
              : comCliente === false
              ? false
              : undefined,
            startDateChecklist,
            endDateChecklist,
            startDateArmazenagem,
            endDateArmazenagem
          );
        } else if (checkboxState.dataSemChecklist) {
          data = await getVehiclesSemChecklist(
            token,
            selectedCompanyId,
            page,
            comCliente === true
              ? true
              : comCliente === false
              ? false
              : undefined,
            startDateChecklist,
            endDateChecklist,
            startDateArmazenagem,
            endDateArmazenagem
          );
        } else {
          data = await getVehiclesNaoEntregue(
            token,
            selectedCompanyId,
            page,
            comCliente === true ? true : comCliente === false ? false : null,
            startDateChecklist,
            endDateChecklist,
            startDateArmazenagem,
            endDateArmazenagem
          );
        }

        if (requestId !== requestSequenceRef.current) {
          return;
        }

        setVehicles(data.data);
        setCurrentPage(data.current_page);
        setTotalPages(data.last_page);
        setTotalVehicles(data.total);
      } catch (error) {
        setLastFetchedPage(null);
        toast.error(
          "Erro ao buscar veículos na base de dados, por favor reinicie o sistema."
        );
        console.error("Erro ao buscar veículos:", error);
      } finally {
        if (requestId === requestSequenceRef.current) {
          setLoadingVehicles(false);
          setIsFetching(false);
        }
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      token,
      selectedCompanyId,
      comCliente,
      startDateChecklist,
      endDateChecklist,
      startDateArmazenagem,
      endDateArmazenagem,
      checkboxState,
      currentPage,
      totalVehicles,
      debouncedSearchValue,
    ]
  );

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);

    return () => clearTimeout(timerId);
  }, [searchValue]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchValue]);

  useEffect(() => {
    const fetchData = async () => {
      const requestId = ++requestSequenceRef.current;
      setIsFetching(true);
      setLoadingVehicles(true);

      try {
        if (!token) throw new Error("Token de autenticação não encontrado");

        let data;
        const formattedStartDateChecklist =
          startDateChecklist?.format("YYYY-MM-DD");
        const formattedEndDateChecklist =
          endDateChecklist?.format("YYYY-MM-DD");
        const formattedStartDateArmazenagem =
          startDateArmazenagem?.format("YYYY-MM-DD");
        const formattedEndDateArmazenagem =
          endDateArmazenagem?.format("YYYY-MM-DD");

        if (debouncedSearchValue.trim().length > 0) {
          data = await searchVehicles(
            token,
            debouncedSearchValue,
            currentPage,
            comCliente,
            selectedCompanyId
          );
        } else if (checkboxState.veiculosEntregues) {
          data = await getVehiclesEntregues(
            token,
            selectedCompanyId,
            currentPage,
            comCliente,
            formattedStartDateChecklist,
            formattedEndDateChecklist,
            formattedStartDateArmazenagem,
            formattedEndDateArmazenagem
          );
        } else if (checkboxState.dataChecklistAtrasado) {
          data = await getVehiclesWithChecklistAtrasado(
            token,
            selectedCompanyId,
            currentPage,
            comCliente,
            formattedStartDateChecklist,
            formattedEndDateChecklist,
            formattedStartDateArmazenagem,
            formattedEndDateArmazenagem
          );
        } else if (checkboxState.dataSemChecklist) {
          data = await getVehiclesSemChecklist(
            token,
            selectedCompanyId,
            currentPage,
            comCliente,
            formattedStartDateChecklist,
            formattedEndDateChecklist,
            formattedStartDateArmazenagem,
            formattedEndDateArmazenagem
          );
        } else {
          data = await getVehiclesNaoEntregue(
            token,
            selectedCompanyId,
            currentPage,
            comCliente,
            formattedStartDateChecklist,
            formattedEndDateChecklist,
            formattedStartDateArmazenagem,
            formattedEndDateArmazenagem
          );
        }

        if (requestId !== requestSequenceRef.current) {
          return;
        }

        setVehicles(data.data);
        setTotalPages(data.last_page);
        setTotalVehicles(data.total);

        if (shouldUpdateUrl) {
          const newParams = new URLSearchParams();
          if (checkboxState.dataChecklistAtrasado)
            newParams.set("filter", "atrasados");
          if (checkboxState.dataSemChecklist)
            newParams.set("filter", "sem_checklist");
          if (selectedCompanyId)
            newParams.set("id_empresa", selectedCompanyId.toString());
          if (currentPage !== 1) newParams.set("page", currentPage.toString());

          if (search !== `?${newParams.toString()}`) {
            navigate(`?${newParams.toString()}`, { replace: true });
          }
        }
      } catch (error) {
        toast.error("Erro ao buscar veículos na base de dados");
        console.error("Erro ao buscar veículos:", error);
      } finally {
        if (requestId === requestSequenceRef.current) {
          setLoadingVehicles(false);
          setIsFetching(false);
          setShouldUpdateUrl(true);
        }
      }
    };

    const timer = setTimeout(() => {
      fetchData();
    }, 300);

    return () => clearTimeout(timer);
  }, [
    token,
    selectedCompanyId,
    comCliente,
    startDateChecklist,
    endDateChecklist,
    startDateArmazenagem,
    endDateArmazenagem,
    checkboxState,
    currentPage,
    debouncedSearchValue,
    navigate,
    search,
    shouldUpdateUrl,
  ]);

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const pageParam = queryParams.get("page");

    if (pageParam) {
      setCurrentPage(Number(pageParam));
    }
  }, [search]);

  useEffect(() => {
    const userDataString = localStorage.getItem("userData");

    if (userDataString) {
      const userData = JSON.parse(userDataString);

      if (userData && userData.admin) {
        setIsAdmin(true);
      }
    }
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        if (!token) {
          throw new Error("Token de autenticação não encontrado");
        }
        const companiesData = await getEmpresasData(token);
        setCompanies(companiesData);
      } catch (error) {
        toast.error("Erro ao buscar empresas.");
        console.error("Erro ao buscar empresas:", error);
      }
    };

    fetchCompanies();
  }, [token]);

  const handleNewVehicle = () => {
    const userData = localStorage.getItem("userData");

    if (userData) {
      const parsedUserData = JSON.parse(userData);
      if (parsedUserData.admin) {
        setOpenAddVehicleModal(true);
      } else {
        navigate("/novoVeiculo");
      }
    } else {
      navigate("/novoVeiculo");
    }
  };

  useEffect(() => {
    if (idEmpresa) {
      const empresaId = parseInt(idEmpresa);
      const selectedCompany = companies.find(
        (company) => company.id === empresaId
      );
      if (selectedCompany) {
        setSelectedCompanyId(selectedCompany.id);
      }
    }
  }, [idEmpresa, companies]);

  const applyFilters = () => {
    handleCloseModal();
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    setCurrentPage(1);

    setCheckboxState({
      veiculosEntregues: false,
      dataArmazenagem: false,
      dataChecklist: false,
      dataSemChecklist: false,
      dataChecklistAtrasado: false,
    });
    setStartDateChecklist(null);
    setEndDateChecklist(null);
    setStartDateArmazenagem(null);
    setEndDateArmazenagem(null);
    setSelectedFilter(null);
    setComCliente(null);

    if (value === "") {
      fetchVehicles(1, "");
    }
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleCheckboxChange = (filterName: keyof CheckboxState) => {
    setCurrentPage(1);

    setCheckboxState((prevState) => ({
      ...prevState,
      [filterName]: !prevState[filterName],
    }));

    if (filterName === "dataArmazenagem") {
      setSelectedFilter("dataArmazenagem");
    } else if (filterName === "dataChecklist") {
      setSelectedFilter("dataChecklist");
    }
  };

  const handleSelectCompany = () => {
    if (selectedCompanyIdModal) {
      navigate(`/novoVeiculo?idEmpresa=${selectedCompanyIdModal}`);
      setOpenAddVehicleModal(false);
    } else {
      toast.error("Por favor, selecione uma empresa.");
    }
  };

  const handleResetFilters = async () => {
    setSelectedFilter(null);
    setStartDateChecklist(null);
    setEndDateChecklist(null);
    setStartDateArmazenagem(null);
    setEndDateArmazenagem(null);
    setCheckboxState({
      veiculosEntregues: false,
      dataArmazenagem: false,
      dataChecklist: false,
      dataSemChecklist: false,
      dataChecklistAtrasado: false,
    });
    setSelectedCompanyId(null);
    setOpenModal(false);
    setComCliente(null);

    try {
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }

      const data = await getVehiclesNaoEntregue(token, null);
      setVehicles(data.data);
      setTotalPages(data.last_page);
      setTotalVehicles(data.total);
      setCurrentPage(1);
      navigate("/veiculos");
    } catch (error) {
      toast.error(
        "Erro ao buscar veículos na base de dados, por favor reinicie o sistema."
      );
      console.error("Erro ao buscar veículos:", error);
    } finally {
    }
  };

  const currentItems = vehicles;

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    if (isFetching || page === lastFetchedPage) return;

    setCurrentPage(page);
    setLastFetchedPage(page);
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }} p={2}>
        <Box sx={{ flexGrow: 1 }}>
          <Toolbar />
          <Box
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Grid container mt={-5} spacing={2} justifyContent="center">
              <HeaderVehicle
                loading={false}
                searchValue={searchValue}
                handleNewVehicle={handleNewVehicle}
                handleSearchChange={handleSearchChange}
                setComCliente={setComCliente}
                comCliente={comCliente}
                vehicles={totalVehicles}
              />
              <FilterVehicle
                loading={false}
                isFetching={isFetching}
                vehicles={vehicles}
                currentPage={currentPage}
                handleChangePage={handleChangePage}
                handleOpenModal={handleOpenModal}
                handleCloseModal={handleCloseModal}
                openModal={openModal}
                isAdmin={isAdmin}
                companies={companies}
                selectedCompanyId={selectedCompanyId}
                setSelectedCompanyId={setSelectedCompanyId}
                checkboxState={checkboxState}
                handleCheckboxChange={handleCheckboxChange}
                selectedFilter={selectedFilter}
                startDateArmazenagem={startDateArmazenagem}
                endDateArmazenagem={endDateArmazenagem}
                setStartDateArmazenagem={setStartDateArmazenagem}
                setEndDateArmazenagem={setEndDateArmazenagem}
                startDateChecklist={startDateChecklist}
                endDateChecklist={endDateChecklist}
                setStartDateChecklist={setStartDateChecklist}
                setEndDateChecklist={setEndDateChecklist}
                applyFilters={applyFilters}
                handleResetFilters={handleResetFilters}
                totalPage={totalPages}
              />
            </Grid>
          </Box>
          <ModalAddVehicle
            open={openAddVehicleModal}
            onClose={handleCloseAddVehicleModal}
            companies={companies}
            selectedCompany={selectedCompany}
            setSelectedCompany={setSelectedCompany}
            setSelectedCompanyId={setSelectedCompanyIdModal}
            handleSelectCompany={handleSelectCompany}
          />
          <Grid container spacing={3} mt={0.2}>
            {loadingVehicles ? (
              <>
                {[...Array(5)].map((_, index) => (
                  <Grid item xs={12} key={index}>
                    <Skeleton
                      variant="rectangular"
                      height={120}
                      sx={{ borderRadius: 5, bgcolor: "grey.400" }}
                    />
                  </Grid>
                ))}
              </>
            ) : currentItems.length === 0 ? (
              <Typography
                variant="h6"
                mt={12}
                sx={{ marginLeft: "auto", marginRight: "auto" }}
              >
                Nenhum veículo encontrado.
              </Typography> 
            ) : (
              currentItems.map((vehicle) => (
                <VehicleCard
                  key={vehicle.id}
                  vehicle={vehicle}
                  expandedPaperId={expandedPaperId}
                  handlePaperClick={handlePaperClick}
                  handleEditButtonClick={handleEditButtonClick}
                  handleAddClientButtonClick={handleAddClientButtonClick}
                  handleChecklistButtonClick={handleChecklistButtonClick}
                  handleViewClientButtonClick={handleViewClientButtonClick}
                  onVehicleTransfer={fetchVehicles}
                />
              ))
            )}
          </Grid>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Veiculos;
